import { MKLocalStorageKeys } from "../../enums";
import { MKUtils } from "./MKUtils";

/**
 * Local storage abstract class.
 * @internal
 */
abstract class LocalStorage<T extends string> {
    private readonly localStorgage: Storage;

    constructor() {
        this.localStorgage = window.localStorage;
    }

    /**
     * Read or get the data from storage.
     * 
     * @param key the key for the item to read from storage.
     * @returns the read data from storage if available.
     */
    protected get(key: T): string | null {
        let item = null;
        if (this.localStorgage) {
            item = this.localStorgage.getItem(key);
        }
        return item;
    }

    /**
     * Write the given data into storage.
     * 
     * @param key the key for the item to save into storage
     * @param value the data to write into storage
     */
    protected set(key: T, value: string) {
        if (this.localStorgage && !!value) {
            this.localStorgage.setItem(key, value);
        }
    }

    /**
     * Remove the item with given key from storage.
     * 
     * @param key the key to remove from local storage.
     */
    protected clearItem(key: T) {
        if (this.localStorgage) {
            this.localStorgage.removeItem(key);
        }
    }

    protected clearItems(keys: T[]) {
        keys.forEach((key) => this.clearItem(key));
    }
}

/**
 * Local storage helper class.
 * @internal
 */
export class MKLocalStorage extends LocalStorage<MKLocalStorageKeys> {
    private static instance?: MKLocalStorage;

    private constructor() {
        super();
    }

    public static getInstance(): MKLocalStorage {
        if (!this.instance) {
            this.instance = new MKLocalStorage();
        }
        return this.instance;
    }

    /////////////////////////////////////
    // Local storage getters & setters //
    /////////////////////////////////////

    public setRegistered(authToken: string): void {
        this.set(MKLocalStorageKeys.Registered, authToken);
    }

    public getRegistered() {
        return this.get(MKLocalStorageKeys.Registered);
    }

    public clearRegistered() {
        this.clearItem(MKLocalStorageKeys.Registered);
    }

    public setDeviceId(deviceId: string) {
        this.set(MKLocalStorageKeys.DeviceId, deviceId);
    }

    public getDeviceId() {
        return this.get(MKLocalStorageKeys.DeviceId);
    }

    public setDeviceProfile(deviceProfile: string) {
        this.set(MKLocalStorageKeys.DeviceProfile, deviceProfile);
    }

    public getDeviceProfile() {
        return this.get(MKLocalStorageKeys.DeviceProfile);
    }

    public setBeaconData(beaconData: any) {
        this.set(MKLocalStorageKeys.BeaconData, JSON.stringify(beaconData));
    }

    public getBeaconData() {
        const beaconData = this.get(MKLocalStorageKeys.BeaconData);
        if (beaconData) return MKUtils.jsonParse(beaconData);
        return null;
    }

    public clearBeaconData() {
        this.clearItem(MKLocalStorageKeys.BeaconData);
    }

    public setClientKeys(clientPrivateKey: string, clientPublicKey: string, timestamp: number): void {
        const value = {
            privateKey: clientPrivateKey,
            publicKey: clientPublicKey,
            timestamp: timestamp
        };
        this.set(MKLocalStorageKeys.ClientKeys, JSON.stringify(value));
    }

    public getClientKeys(): { privateKey: string; publicKey: string; timestamp: number } | null {
        const storedValue = this.get(MKLocalStorageKeys.ClientKeys);
        if (!storedValue) return null;
        const parsed = JSON.parse(storedValue);
        return {
            privateKey: parsed.privateKey,
            publicKey: parsed.publicKey,
            timestamp: parsed.timestamp
        };
    }

    public clearClientPrivatekey() {
        this.clearItem(MKLocalStorageKeys.ClientKeys);
    }
}